/**
 * Subscription-status bus (Seven Free Days, 2026-09-26).
 *
 * Every screen that shows subscription state calls `useSubscription()` and holds
 * its OWN copy of /api/user/subscription-status, fetched on mount,
 * pull-to-refresh or a purchase. So when the 7-day clock started on a
 * goal-confirm turn, the chat knew (the turn's `trialClockStarted` metadata) but
 * Account and the Subscription drawer kept the pre-trial copy until a relaunch.
 *
 * One channel, broadcast: a publisher says the server-side subscription state
 * changed; every mounted `useSubscription()` refetches. Built on
 * DeviceEventEmitter, the same way recalCompletionBus is.
 *
 * Publishers: the chat, on a turn whose metadata carries `trialClockStarted`
 * (utils/trialClockSignal.ts decides which turns).
 * Subscribers: hooks/useSubscription.ts, one per mounted instance.
 */

import { DeviceEventEmitter, EmitterSubscription } from 'react-native';

export const SUBSCRIPTION_STATUS_CHANNEL = 'cedar:subscriptionStatusChanged';

export interface SubscriptionStatusChangedEvent {
  /** Why the state changed, for logs: e.g. 'trial_clock_started'. */
  reason: string;
  /** epoch ms at publication */
  timestamp: number;
}

/** Tell every mounted subscription surface to refetch. Never throws. */
export function publishSubscriptionStatusChanged(reason: string): void {
  const event: SubscriptionStatusChangedEvent = { reason, timestamp: Date.now() };
  try {
    DeviceEventEmitter.emit(SUBSCRIPTION_STATUS_CHANNEL, event);
  } catch (e) {
    // A bus failure must never break the chat turn that published it.
    console.warn('[subscriptionStatusBus] publish failed:', e);
  }
}

/** Subscribe; release the returned handle in the effect cleanup. */
export function subscribeSubscriptionStatusChanged(
  handler: (event: SubscriptionStatusChangedEvent) => void,
): EmitterSubscription {
  return DeviceEventEmitter.addListener(SUBSCRIPTION_STATUS_CHANNEL, handler);
}
